// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { GhostButton, StatusBadge, XIcon } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import {
  canonicalToken,
  defaultFormatFor,
  filterVariables,
  findMentionQuery,
  findVariable,
  insertTokenAtRange,
  parseTemplateTokens,
  removeToken,
  replaceTokenFormat,
  validateTemplateTokens,
  variableAllowsFormat,
  variableLabel,
} from "./templateVariables";

// Same recipe as the design-system TextArea (md size) so the field looks
// identical — the design system has no "textarea with inline highlights".
const FIELD_BASE =
  "w-full border rounded-md shadow-sm transition-colors duration-200 focus:outline-none px-3 py-2 text-base leading-6";
const FIELD_STATE = {
  normal:
    "border-gray-300 dark:border-dark-600 text-gray-900 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400",
  error:
    "border-red-300 dark:border-red-700 text-gray-900 dark:text-dark-100 placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-400",
  disabled: "border-gray-300 dark:border-dark-700 text-gray-500 dark:text-dark-400 cursor-not-allowed",
};

const TYPE_BADGE = {
  text: { label: "text", variant: "neutral" },
  number: { label: "number", variant: "info" },
  date: { label: "date", variant: "success" },
};

/** Backdrop text: identical characters, tokens wrapped in highlight pills. */
const HighlightedText = ({ text, variables }) => {
  const tokens = parseTemplateTokens(text);
  if (tokens.length === 0) return <>{text}</>;
  const parts = [];
  let cursor = 0;
  tokens.forEach((token, index) => {
    if (token.start > cursor) parts.push(<React.Fragment key={`t${index}`}>{text.slice(cursor, token.start)}</React.Fragment>);
    const valid = validateTemplateTokens(token.raw, variables).length === 0;
    parts.push(
      <mark
        key={`m${index}`}
        className={`rounded px-0 text-transparent ${
          valid ? "bg-blue-200/70 dark:bg-blue-500/30" : "bg-red-200/70 dark:bg-red-500/30"
        }`}
      >
        {token.raw}
      </mark>
    );
    cursor = token.end;
  });
  if (cursor < text.length) parts.push(<React.Fragment key="tail">{text.slice(cursor)}</React.Fragment>);
  return <>{parts}</>;
};

HighlightedText.propTypes = { text: PropTypes.string.isRequired, variables: PropTypes.array };

/**
 * A textarea for automation titles/bodies with message variables:
 *  - `@` opens the variable menu (filter as you type, ↑/↓, Enter, Esc);
 *    formattable variables show their formats (default preselected) before
 *    inserting `{{key - FORMAT}}`; text variables insert `{{key}}` directly;
 *  - tokens are highlighted in place and listed as chips under the field,
 *    each with a format dropdown (when the variable takes one) and a remove;
 *  - an "Insert variable" button opens the same menu at the caret.
 * `singleLine` makes it behave like an input (no newlines) — used for titles.
 */
const VariableTextArea = ({
  id,
  value = "",
  onChange,
  variables = [],
  singleLine = false,
  rows = 6,
  placeholder = "",
  disabled = false,
  error = "",
  helperText = "",
  maxLength,
  "aria-label": ariaLabel,
  className = "",
}) => {
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);
  const menuRef = useRef(null);
  const pendingCaret = useRef(null);

  const [menu, setMenu] = useState(null); // { start, end, query, step: "variables"|"formats", variable, activeIndex }
  const text = String(value || "");
  const tokens = useMemo(() => parseTemplateTokens(text), [text]);
  const problems = useMemo(() => validateTemplateTokens(text, variables), [text, variables]);
  const tokenProblem = (token) => problems.find((p) => p.token.start === token.start)?.message || "";

  // Place the caret after a programmatic insertion once React has re-rendered the value.
  useLayoutEffect(() => {
    if (pendingCaret.current == null || !textareaRef.current) return;
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    try {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(caret, caret);
    } catch {
      /* jsdom / unsupported */
    }
  }, [text]);

  const syncScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const closeMenu = useCallback(() => setMenu(null), []);

  const openMenuAt = (start, end, query = "") => {
    setMenu({ start, end, query, step: "variables", variable: null, activeIndex: 0 });
  };

  const refreshMenuFromCaret = (nextText, caret) => {
    const mention = findMentionQuery(nextText, caret);
    if (mention) {
      setMenu((prev) => ({
        start: mention.start,
        end: caret,
        query: mention.query,
        step: prev && prev.step === "formats" && prev.start === mention.start ? prev.step : "variables",
        variable: prev && prev.start === mention.start ? prev.variable : null,
        activeIndex: 0,
      }));
    } else {
      setMenu(null);
    }
  };

  const emit = (nextText, caret) => {
    pendingCaret.current = caret;
    onChange?.(nextText);
  };

  const handleChange = (e) => {
    let next = e.target.value;
    if (singleLine) next = next.replace(/[\r\n]+/g, " ");
    const caret = e.target.selectionStart ?? next.length;
    onChange?.(next);
    refreshMenuFromCaret(next, caret);
  };

  const insertVariable = (variable, format) => {
    if (!menu) return;
    const token = canonicalToken(variable.key, format || null);
    const { text: nextText, caret } = insertTokenAtRange(text, menu.start, menu.end, token);
    setMenu(null);
    emit(nextText, caret);
  };

  const chooseVariable = (variable) => {
    if (variableAllowsFormat(variable)) {
      const formats = variable.formats || [];
      const defaultKey = defaultFormatFor(variable);
      const activeIndex = Math.max(0, formats.findIndex((f) => f.key === defaultKey));
      setMenu((prev) => (prev ? { ...prev, step: "formats", variable, activeIndex } : prev));
    } else {
      insertVariable(variable, null);
    }
  };

  const filtered = useMemo(() => (menu ? filterVariables(variables, menu.query) : []), [menu, variables]);
  const menuItems = menu ? (menu.step === "formats" ? menu.variable?.formats || [] : filtered) : [];

  const handleKeyDown = (e) => {
    if (menu) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (menuItems.length === 0) return;
        const delta = e.key === "ArrowDown" ? 1 : -1;
        setMenu((prev) => (prev ? { ...prev, activeIndex: (prev.activeIndex + delta + menuItems.length) % menuItems.length } : prev));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (menuItems.length === 0) {
          if (e.key === "Enter" && singleLine) e.preventDefault();
          return;
        }
        e.preventDefault();
        const item = menuItems[Math.min(menu.activeIndex, menuItems.length - 1)];
        if (menu.step === "formats") insertVariable(menu.variable, item.key);
        else chooseVariable(item);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (menu.step === "formats") setMenu((prev) => (prev ? { ...prev, step: "variables", variable: null, activeIndex: 0 } : prev));
        else closeMenu();
        return;
      }
    }
    if (singleLine && e.key === "Enter") e.preventDefault();
  };

  const handleInsertButton = () => {
    const el = textareaRef.current;
    const caret = el ? el.selectionStart ?? text.length : text.length;
    // Behave exactly like typing "@" at the caret: the token replaces that range.
    const before = text.slice(0, caret);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const nextText = before + (needsSpace ? " @" : "@") + text.slice(caret);
    const atIndex = before.length + (needsSpace ? 1 : 0);
    onChange?.(nextText);
    pendingCaret.current = atIndex + 1;
    openMenuAt(atIndex, atIndex + 1, "");
  };

  // Close the menu on outside clicks.
  useEffect(() => {
    if (!menu) return undefined;
    const onDocClick = (event) => {
      if (menuRef.current?.contains(event.target) || textareaRef.current?.contains(event.target)) return;
      closeMenu();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menu, closeMenu]);

  const changeTokenFormat = (token, format) => {
    const nextText = replaceTokenFormat(text, token, format);
    emit(nextText, Math.min(nextText.length, token.start + canonicalToken(token.key, format || null).length));
  };

  const deleteToken = (token) => emit(removeToken(text, token), token.start);

  const stateClass = disabled ? FIELD_STATE.disabled : error ? FIELD_STATE.error : FIELD_STATE.normal;
  const listboxId = id ? `${id}-variable-menu` : undefined;
  const helperId = id ? `${id}-helper` : undefined;
  const errorId = id ? `${id}-error` : undefined;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className={`relative rounded-md bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm ${disabled ? "bg-gray-100 dark:bg-dark-800" : ""}`}>
        {/* Highlight backdrop: same box + font as the textarea; only the pills are visible. */}
        <div
          ref={backdropRef}
          aria-hidden="true"
          className={`${FIELD_BASE} border-transparent shadow-none absolute inset-0 overflow-hidden pointer-events-none whitespace-pre-wrap break-words text-transparent`}
        >
          <HighlightedText text={text} variables={variables} />
          {/* Trailing newline keeps the backdrop the same height as the textarea on a final empty line. */}
          {text.endsWith("\n") ? "​" : null}
        </div>
        <textarea
          ref={textareaRef}
          id={id}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onScroll={syncScroll}
          onClick={(e) => refreshMenuFromCaret(text, e.target.selectionStart ?? text.length)}
          onKeyUp={(e) => {
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) refreshMenuFromCaret(text, e.target.selectionStart ?? text.length);
          }}
          rows={singleLine ? 1 : rows}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
          aria-describedby={[error ? errorId : null, helperText && !error ? helperId : null].filter(Boolean).join(" ") || undefined}
          aria-autocomplete="list"
          aria-controls={menu ? listboxId : undefined}
          spellCheck
          className={`${FIELD_BASE} ${stateClass} relative bg-transparent ${singleLine ? "resize-none overflow-hidden whitespace-nowrap" : "resize-y"}`}
          style={singleLine ? { whiteSpace: "pre", overflowX: "auto" } : undefined}
        />

        {menu ? (
          <div
            ref={menuRef}
            id={listboxId}
            role="listbox"
            aria-label={menu.step === "formats" ? `Formats for ${menu.variable?.key}` : "Variables"}
            className={`absolute left-0 right-0 top-full mt-1 z-40 max-h-72 overflow-y-auto rounded-xl border ${borders.default} ${surfaces.elevated} shadow-lg py-1`}
          >
            <div className={`px-3 py-1.5 ${typography.caption} ${textColors.tertiary} border-b ${borders.light} flex items-center justify-between gap-2`}>
              {menu.step === "formats" ? (
                <>
                  <span className="truncate">
                    Format for <span className={`font-medium ${textColors.primary}`}>{menu.variable?.key}</span>
                  </span>
                  <button
                    type="button"
                    className={`${typography.caption} ${textColors.secondary} underline`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setMenu((prev) => (prev ? { ...prev, step: "variables", variable: null, activeIndex: 0 } : prev))}
                  >
                    Back
                  </button>
                </>
              ) : (
                <span>
                  Insert a variable{menu.query ? ` matching “${menu.query}”` : ""} — ↑↓ to move, Enter to pick, Esc to close
                </span>
              )}
            </div>
            {menuItems.length === 0 ? (
              <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.muted}`}>No variables match.</div>
            ) : menu.step === "formats" ? (
              menuItems.map((format, index) => {
                const active = index === menu.activeIndex;
                const isDefault = format.key === defaultFormatFor(menu.variable);
                return (
                  <div
                    key={format.key}
                    role="option"
                    aria-selected={active}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setMenu((prev) => (prev ? { ...prev, activeIndex: index } : prev))}
                    onClick={() => insertVariable(menu.variable, format.key)}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${active ? "bg-blue-50 dark:bg-blue-900/30" : ""}`}
                  >
                    <span className={`${typography.bodySmall} ${textColors.primary} font-mono`}>{format.key}</span>
                    <span className={`${typography.caption} ${textColors.secondary} truncate`}>
                      {format.example}
                      {isDefault ? <span className={`ml-2 ${textColors.muted}`}>(default)</span> : null}
                    </span>
                  </div>
                );
              })
            ) : (
              menuItems.map((variable, index) => {
                const active = index === menu.activeIndex;
                const badge = TYPE_BADGE[variable.type] || TYPE_BADGE.text;
                return (
                  <div
                    key={variable.key}
                    role="option"
                    aria-selected={active}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setMenu((prev) => (prev ? { ...prev, activeIndex: index } : prev))}
                    onClick={() => chooseVariable(variable)}
                    className={`px-3 py-2 cursor-pointer ${active ? "bg-blue-50 dark:bg-blue-900/30" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`${typography.bodySmall} ${textColors.primary} font-medium truncate`}>
                        {variable.label} <span className={`${textColors.tertiary} font-mono font-normal`}>{variable.key}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 shrink-0">
                        {variableAllowsFormat(variable) ? (
                          <span className={`${typography.caption} ${textColors.muted}`}>formats ›</span>
                        ) : null}
                        <StatusBadge size="sm" variant={badge.variant}>
                          {badge.label}
                        </StatusBadge>
                      </span>
                    </div>
                    {variable.description ? (
                      <div className={`${typography.caption} ${textColors.tertiary} truncate`}>{variable.description}</div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {error ? (
            <p id={errorId} className={`${typography.caption} text-red-600 dark:text-red-400`} role="alert">
              {error}
            </p>
          ) : helperText ? (
            <p id={helperId} className={`${typography.caption} ${textColors.tertiary}`}>
              {helperText}
            </p>
          ) : null}
        </div>
        <GhostButton size="sm" onClick={handleInsertButton} disabled={disabled} aria-label="Insert variable" title="Insert a variable (or type @)">
          @ Insert variable
        </GhostButton>
      </div>

      {tokens.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Variables used">
          {tokens.map((token, index) => {
            const variable = findVariable(variables, token.key);
            const problem = tokenProblem(token);
            const allowsFormat = variableAllowsFormat(variable);
            return (
              <li
                key={`${token.start}-${index}`}
                className={`inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-0.5 ${
                  problem
                    ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20"
                    : "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20"
                }`}
                title={problem || variable?.description || token.key}
              >
                <span className={`${typography.caption} ${problem ? "text-red-700 dark:text-red-300" : "text-blue-800 dark:text-blue-200"} font-mono whitespace-nowrap`}>
                  {variableLabel(token.key, token.format)}
                </span>
                {allowsFormat ? (
                  <select
                    aria-label={`Format for ${token.key}`}
                    value={token.format || defaultFormatFor(variable) || ""}
                    onChange={(e) => changeTokenFormat(token, e.target.value)}
                    disabled={disabled}
                    className={`${typography.caption} rounded border ${borders.default} ${surfaces.primary} ${textColors.primary} px-1 py-0.5`}
                  >
                    {(variable.formats || []).map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.key} — {f.example}
                      </option>
                    ))}
                  </select>
                ) : null}
                {problem ? <span className={`${typography.caption} text-red-700 dark:text-red-300`}>{problem}</span> : null}
                <button
                  type="button"
                  onClick={() => deleteToken(token)}
                  disabled={disabled}
                  aria-label={`Remove ${canonicalToken(token.key, token.format)}`}
                  title="Remove variable"
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${textColors.secondary} ${surfaces.hover}`}
                >
                  <XIcon size="xs" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

VariableTextArea.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  variables: PropTypes.array,
  singleLine: PropTypes.bool,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  maxLength: PropTypes.number,
  "aria-label": PropTypes.string,
  className: PropTypes.string,
};

export default VariableTextArea;
